import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services';
import { useAuthStore } from '@shared/stores/authStore';
import { QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { startTransition } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useToast } from '@shared/hooks/use-toast';
import { AuthApiAdapter } from '../adapters/AuthApiAdapter';
import { ForgotPasswordRequest, LoginRequest, ResetPasswordRequest } from '../models/Auth';
import { AuthService } from '../services/AuthService';

const AUTH_QUERY_KEYS = {
  user: ['auth', 'user'],
  currentUser: ['auth', 'current-user'],
} as const;
const CLIENT_DETAIL_PATH_REGEX = /^\/clients\/\d+$/;

/**
 * Cambia de team y sincroniza token, usuario y rol en el store sin toasts ni invalidaciones.
 * Útil para flujos internos (p. ej. crear pack en contexto de un cliente 3PL).
 */
export async function switchTeamAndSyncSession(
  teamId: number,
  deps: { authService: AuthService; queryClient: QueryClient },
): Promise<void> {
  const { authService, queryClient } = deps;
  const authStore = useAuthStore.getState();
  const response = await authService.switchTeam(teamId);
  if (!response?.token) {
    throw new Error('No se recibió token al cambiar de equipo');
  }
  authStore.setToken(response.token);
  authStore.setAuthenticated(true);
  const userData = await authService.getCurrentUser();
  const data = userData as any;
  authStore.setUser(data.user ?? data);
  authStore.setTeam(data.team ?? null);
  authStore.setRole(data.role ?? null);
  queryClient.setQueryData(AUTH_QUERY_KEYS.currentUser, data);
}

// ?? Hook para login
export const useLogin = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setUser, setToken, setAuthenticated, setTeam, setRole } = useAuthStore();
  const queryClient = useQueryClient();

  const httpClient = createHttpClient(apiClientWH);
  const apiAdapter = new AuthApiAdapter(httpClient);
  const authService = new AuthService(apiAdapter);

  // ?? Función para determinar la URL de redirección
  const getRedirectUrl = () => {
    const redirectUrl = searchParams.get('redirect');

    // Si no hay URL de redirección, ir a inicio
    if (!redirectUrl) {
      return '/home';
    }

    // Si la URL de redirección es login, ir a inicio (evitar loop)
    if (redirectUrl.startsWith('/login')) {
      return '/home';
    }

    // Si viene un detalle de cliente tras login, forzar listado para evitar exposición.
    if (CLIENT_DETAIL_PATH_REGEX.test(redirectUrl)) {
      return '/clients';
    }

    // Si es una URL válida, usarla
    return redirectUrl;
  };

  return useMutation({
    mutationFn: (credentials: LoginRequest) => authService.login(credentials),
    onSuccess: async (response) => {
      // ?? 1. Guardar token primero
      setToken(response.token.token);
      setAuthenticated(true);

      try {
        // ?? 2. Obtener datos del usuario con el token guardado
        const userData = await authService.getCurrentUser();

        // ?? Desestructurar y guardar correctamente
        setUser((userData as any).user);
        setTeam((userData as any).team);
        setRole((userData as any).role);

        // ?? 3. Invalidar cache de usuario
        queryClient.invalidateQueries({ queryKey: AUTH_QUERY_KEYS.user });

        // ?? 4. Redirigir a la URL apropiada
        const finalRedirectUrl = getRedirectUrl();
        navigate(finalRedirectUrl, { replace: true });
      } catch (userError) {
        console.warn('Error obteniendo usuario después del login:', userError);
        // Aún redirigimos, el usuario se puede cargar después
        const finalRedirectUrl = getRedirectUrl();
        navigate(finalRedirectUrl, { replace: true });
      }
    },
    onError: (error) => {
      console.error('Error en login:', error);
    },
  });
};

// ?? Hook para logout
export const useLogout = () => {
  const navigate = useNavigate();
  const { logout: clearAuthStore } = useAuthStore();
  const queryClient = useQueryClient();

  const httpClient = createHttpClient(apiClientWH);
  const apiAdapter = new AuthApiAdapter(httpClient);
  const authService = new AuthService(apiAdapter);

  const finalizeLogout = () => {
    startTransition(() => {
      navigate('/login', { replace: true });
      clearAuthStore();
      queryClient.clear();
    });
  };

  return useMutation({
    mutationFn: () => authService.logout(),
    onSuccess: finalizeLogout,
    onError: (error) => {
      console.warn('[logout] onError servidor:', error);
      finalizeLogout();
    },
  });
};

// ?? Hook para obtener usuario actual
export const useCurrentUser = (enabled: boolean = true) => {
  const { token, setSettings } = useAuthStore();

  return useQuery({
    queryKey: AUTH_QUERY_KEYS.currentUser,
    queryFn: async () => {
      // Obtener la respuesta completa de /me que incluye settings
      const response = await apiClientWH.get<any>('/me');
      // Guardar settings en el store
      if (response.data.settings) {
        setSettings(response.data.settings);
      }
      // Retornar solo el user para mantener compatibilidad
      return response.data.user || response.data;
    },
    enabled: enabled && !!token,
    staleTime: 5 * 60 * 1000, // 5 minutos
    retry: (failureCount, error: any) => {
      // No reintentar en errores 401/403
      if (error?.response?.status === 401 || error?.response?.status === 403) {
        return false;
      }
      return failureCount < 3;
    },
  });
};

// ?? Hook combinado para facilidad de uso
export const useAuth = () => {
  const user = useAuthStore((s) => s.user);
  const token = useAuthStore((s) => s.token);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const isImpersonating = useAuthStore((s) => s.isImpersonating);
  const team = useAuthStore((s) => s.team);
  const setUser = useAuthStore((s) => s.setUser);
  const setTeam = useAuthStore((s) => s.setTeam);
  const loginMutation = useLogin();
  const logoutMutation = useLogout();
  const currentUserQuery = useCurrentUser();

  // ? Sincronizar authStore.user y team con currentUserQuery.data
  React.useEffect(() => {
    if (currentUserQuery.data) {
      // ?? IMPORTANTE: No sincronizar durante impersonación
      if (isImpersonating) {
        console.log('?? useAuth: Skip sync - en modo impersonación');
        return;
      }

      // Sincronizar usuario
      if (!user || user.id !== currentUserQuery.data.id) {
        console.log('?? useAuth: Sincronizando usuario en store', currentUserQuery.data);
        setUser(currentUserQuery.data);
      }

      // Sincronizar team
      if (currentUserQuery.data.team && (!team || team.id !== currentUserQuery.data.team.id)) {
        console.log('?? useAuth: Sincronizando team en store', currentUserQuery.data.team);
        setTeam(currentUserQuery.data.team);
      }

      // Los settings ya se guardaron en el queryFn de useCurrentUser
    }
  }, [currentUserQuery.data, isImpersonating, user, team, setUser, setTeam]);

  return {
    // Estado
    user,
    token,
    isAuthenticated,

    // Queries
    currentUser: currentUserQuery.data,
    isLoadingUser: currentUserQuery.isLoading,
    userError: currentUserQuery.error,

    // Mutations
    login: loginMutation.mutateAsync,
    logout: logoutMutation.mutateAsync,

    // Estados de loading
    isLoggingIn: loginMutation.isPending,
    isLoggingOut: logoutMutation.isPending,

    // Errores
    loginError: loginMutation.error,
    logoutError: logoutMutation.error,

    // Refetch user
    refetchUser: currentUserQuery.refetch,
  };
};

// ?? Hook para solicitar reset de contraseña
export const useRequireResetPassword = () => {
  const httpClient = createHttpClient(apiClientWH);
  const apiAdapter = new AuthApiAdapter(httpClient);
  const authService = new AuthService(apiAdapter);

  return useMutation({
    mutationFn: (request: ForgotPasswordRequest) => authService.requireResetPassword(request),
    onError: (error) => {
      console.error('Error al solicitar reset de contraseña:', error);
    },
  });
};

// ?? Hook para confirmar reset de contraseña
export const useResetPassword = () => {
  const navigate = useNavigate();
  const httpClient = createHttpClient(apiClientWH);
  const apiAdapter = new AuthApiAdapter(httpClient);
  const authService = new AuthService(apiAdapter);

  return useMutation({
    mutationFn: (request: ResetPasswordRequest) => authService.confirmResetPassword(request),
    onSuccess: () => {
      // Redirigir a login con mensaje de éexito
      navigate('/login?reset=success', { replace: true });
    },
    onError: (error) => {
      console.error('Error al resetear contraseña:', error);
    },
  });
};

// Hook para cambiar de team
export const useSwitchTeam = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation('menu');
  const httpClient = createHttpClient(apiClientWH);
  const apiAdapter = new AuthApiAdapter(httpClient);
  const authService = new AuthService(apiAdapter);
  const authStore = useAuthStore();

  return useMutation({
    onMutate: () => {
      const { team, role } = useAuthStore.getState();
      // Mostrar overlay reutilizando AuthInitializer (team/role en null)
      authStore.setTeam(null);
      authStore.setRole(null);
      return { previousTeam: team, previousRole: role } as const;
    },
    mutationFn: async (teamId: number) => {
      // 1. Cambiar de team y recibir nuevo token
      const response = await authService.switchTeam(teamId);
      if (!response?.token) {
        throw new Error('No se recibio token al cambiar de equipo');
      }

      // 2. Guardar token y marcar autenticado
      authStore.setToken(response.token);
      authStore.setAuthenticated(true);

      // 3. Traer datos actualizados del usuario (incluye team/role actuales)
      const userData = await authService.getCurrentUser();
      const data = userData as any;

      // 4. Actualizar store completo
      authStore.setUser(data.user ?? data);
      authStore.setTeam(data.team ?? null);
      authStore.setRole(data.role ?? null);
      queryClient.setQueryData(AUTH_QUERY_KEYS.currentUser, data);

      return { response, userData };
    },
    onSuccess: () => {
      // 5. Refrescar queries dependientes del team/rol/token
      queryClient.invalidateQueries();

      // 6. Mostrar feedback
      toast({
        title: t('switchTeam.success.title'),
        description: t('switchTeam.success.description'),
        variant: 'default',
      });
    },
    onError: (error: any, _teamId, context) => {
      // Restaurar estado previo para no quedar en loading
      if (context) {
        authStore.setTeam(context.previousTeam);
        authStore.setRole(context.previousRole);
      }

      let errorMessage = t('switchTeam.error.unexpected');
      if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      toast({
        title: t('switchTeam.error.title'),
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });
};

// Hook especifico para AuthInitializer (sin navegacion)
export const useAuthData = () => {
  const user = useAuthStore((s) => s.user);
  const token = useAuthStore((s) => s.token);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const isImpersonating = useAuthStore((s) => s.isImpersonating);
  const team = useAuthStore((s) => s.team);
  const role = useAuthStore((s) => s.role);
  const setUser = useAuthStore((s) => s.setUser);
  const setTeam = useAuthStore((s) => s.setTeam);
  const setRole = useAuthStore((s) => s.setRole);
  const setSettings = useAuthStore((s) => s.setSettings);
  const currentUserQuery = useCurrentUser();

  // Sincronizar authStore.user, team y role con currentUserQuery.data
  React.useEffect(() => {
    if (currentUserQuery.data) {
      const data = currentUserQuery.data as any;

      // IMPORTANTE: No sincronizar durante impersonacion
      // Durante impersonacion, los datos ya estan correctos en el store
      if (isImpersonating) {
        console.log('useAuthData: Skip sync - en modo impersonacion');
        return;
      }

      // Sincronizar usuario
      if (data.user && (!user || user.id !== data.user.id)) {
        console.log('useAuthData: Sincronizando usuario en store', data.user);
        setUser(data.user);
      }

      // Sincronizar team
      if (data.team && (!team || team.id !== data.team.id)) {
        console.log('useAuthData: Sincronizando team en store', data.team);
        setTeam(data.team);
      }

      // Sincronizar role
      if (data.role && (!role || role.id !== data.role.id)) {
        console.log('useAuthData: Sincronizando role en store', data.role);
        setRole(data.role);
      }

      // Sincronizar settings
      if (data.settings) {
        console.log('useAuthData: Sincronizando settings en store', data.settings);
        setSettings(data.settings);
      }
    }
  }, [currentUserQuery.data, isImpersonating, user, team, role, setUser, setTeam, setRole, setSettings]);

  return {
    // Estado
    user,
    token,
    isAuthenticated,

    // Query data
    currentUser: currentUserQuery.data,
    isLoadingUser: currentUserQuery.isLoading,
    userError: currentUserQuery.error,

    // Refetch user
    refetchUser: currentUserQuery.refetch,
  };
};
